import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn } from "@angular/forms";
import { isEmailValid } from "@obg/ngx.common";
import { Jurisdiction } from "@obg/ngx.core";
import { validationRegex } from "@obg/ngx.global";
import { get, isFunction } from "lodash-es";
import { Observable, of } from "rxjs";
import { map } from "rxjs/operators";

import { isPasswordValid } from "./password/password-validator";
import { PASSWORD_MIN_LENGTH } from "./validation.const";

export type PasswordStrengthValidityType = {
	passwordValid?: boolean;
	passwordNumber?: boolean;
	passwordUppercase?: boolean;
	passwordLowercase?: boolean;
	passwordAllowedChars?: boolean;
	passwordMinimumLength?: boolean;
	passwordMaximumLength?: boolean;
	passwordSpecialChar?: boolean;
} | null;

export type UsernameValidityType = { usernameInvalid: boolean } | null;

export type NonNumericValidityType = { nonNumeric: boolean } | null;

export type EmailValidityType = { emailInvalid: boolean } | null;

export type CurrencyAmountValidityType = { [key: string]: boolean } | null;

export type NumericNotZeroValidityType = { numericNotZero: boolean } | null;

export type NumberSmallerOrEqualValidityType = { numberSmallerOrEqual: boolean } | null;

export type NumberBiggerOrEqualValidityType = { numberBiggerOrEqual: boolean } | null;

export type ExactLengthValidityType = { invalidLength: true } | null;

export type MaxValueValidityType = { maxValueExceeded: boolean } | null;

export type dateEarlierOrEqualValidityType = { dateEarlierOrEqual: boolean } | null;

export type dateLaterOrEqualValidityType = { dateLaterOrEqual: boolean } | null;

export interface PasswordValidity {
	weak: RegExp;
	minimumLength: number;
	maximumLength: number | undefined;
}

export const OBG_VALIDATORS = {
	numeric: (control: AbstractControl): { numeric: boolean } | null =>
		control.value && control.value.toString().match(validationRegex.number) ? null : { numeric: false },

	nullableNumeric: (control: AbstractControl): { numeric: boolean } | null =>
		control.value ? OBG_VALIDATORS.numeric(control) : null,

	nonNumeric: (control: AbstractControl): NonNumericValidityType =>
		control.value && control.value.match(validationRegex.noNumber) ? null : { nonNumeric: false },

	nullableNonNumeric: (control: AbstractControl): NonNumericValidityType =>
		control.value ? OBG_VALIDATORS.nonNumeric(control) : null,

	whitespace: (control: AbstractControl): { whitespace: boolean } | null =>
		control.value && control.value.trim().length > 0 ? null : { whitespace: false },

	whitespaceForNonRequired: (control: AbstractControl): { whitespace: boolean } | null => {
		if (control.value == null || control.value === "") {
			return null;
		}
		return control.value.trim().length > 0 ? null : { whitespace: true };
	},

	nationalId:
		(jurisdiction: string) =>
		(control: AbstractControl): { nationalId: false } | null =>
			control.value && control.value.match(get(validationRegex.nationalId, jurisdiction.toLowerCase()))
				? null
				: { nationalId: false },

	matches:
		(matches: AbstractControl | undefined) =>
		(control: AbstractControl): { matches: false } | null => {
			if (!matches || !matches.value || control.value === matches.value) {
				return null;
			}

			return {
				matches: false,
			};
		},

	compareContains:
		(compareFnOrValue: (() => string | undefined) | string | undefined, errorKey = "distinct") =>
		(control: AbstractControl): ValidationErrors | null => {
			const compareValue = isFunction(compareFnOrValue) ? compareFnOrValue() : compareFnOrValue;
			if (!compareValue) {
				return null;
			}

			if (control.value && control.value.toLowerCase().indexOf(compareValue.toLowerCase()) < 0) {
				return null;
			}

			return {
				[errorKey]: false,
			};
		},

	passwordStrength: (control: AbstractControl, jurisdiction?: Jurisdiction): PasswordStrengthValidityType => {
		if (!control.value) {
			return { passwordValid: false };
		}

		return getPasswordErrors(control.value, true, jurisdiction);
	},

	containsDate:
		(contains: AbstractControl | undefined) =>
		(control: AbstractControl): { containsDate: false } | null => {
			const controlValue = control?.value ? control.value.toString() : null;
			const containsValue = contains?.value
				? contains.value.toString().substring(0, contains.value.toString().indexOf("T")).split("-")
				: null;

			if (!containsValue || !controlValue) {
				return null;
			}

			const yearValue = containsValue[0];
			const monthValue = containsValue[1];
			const dayValue = containsValue[2];
			const permutations = [dayValue + monthValue, monthValue + dayValue, yearValue, yearValue.substring(2)];

			return permutations.find((permutation) => controlValue.match(permutation)) == null
				? null
				: { containsDate: false };
		},

	containsUsername:
		(contains: AbstractControl | undefined) =>
		(control: AbstractControl): { containsUsername: false } | null => {
			const containsValue = contains?.value ? contains.value.toString().toLowerCase() : null;
			const controlValue = control?.value ? control.value.toString().toLowerCase() : null;

			if (!containsValue || !controlValue) {
				return null;
			}

			// Remove domain from email
			const usernameString = containsValue.substring(0, containsValue.lastIndexOf("@"));

			return controlValue.match(usernameString) == null ? null : { containsUsername: false };
		},

	containsMultipleNames:
		(contains: AbstractControl | undefined) =>
		(control: AbstractControl): { containsMultipleNames: false } | null => {
			const containsValue = contains?.value ? contains.value.toString().toLowerCase() : null;
			const controlValue = control?.value ? control.value.toString().toLowerCase() : null;

			if (!containsValue || !controlValue) {
				return null;
			}

			const nameString = containsValue
				// Replace non-alphanumeric characters with a space
				.replace(/[-:\]\[.]/g, " ")
				// Replace multiple spaces with one space
				.replace(/\s+/g, " ")
				// Remove spaces from before and after string
				.trim();

			if (!nameString) {
				return null;
			}

			const names: [] = nameString.split(" ");

			return names.find((name) => controlValue.match(name)) == null ? null : { containsMultipleNames: false };
		},

	containsSpanishCharacters:
		(contains: AbstractControl | undefined) =>
		(control: AbstractControl): { containsSpanishCharacters: false } | null => {
			const containsValue = contains?.value ? contains.value.toString().toLowerCase() : null;
			const controlValue = control?.value ? control.value.toString().toLowerCase() : null;

			if (!containsValue || !controlValue) {
				return null;
			}

			const nameString = containsValue
				.replace(/[áàäâ]/g, "a")
				.replace(/[éèëê]/g, "e")
				.replace(/[íìïî]/g, "i")
				.replace(/[óòöô]/g, "o")
				.replace(/[úùûü]/g, "u")
				.replace(/[ç]/g, "c")
				.replace(/[ñ]/g, "n")
				.trim();

			return controlValue.match(nameString) == null ? null : { containsSpanishCharacters: false };
		},

	username: (control: AbstractControl): UsernameValidityType =>
		control.value && control.value.match(validationRegex.username) ? null : { usernameInvalid: false },

	noComma: (control: AbstractControl) =>
		control.value && control.value.includes(",") ? { noSpecialCharacters: false } : null,

	noCommaOrApostrophe: (control: AbstractControl) =>
		control.value && /[,']/.test(control.value) ? { noSpecialCharacters: false } : null,

	duplicationCheck: (validationFn: (value: string) => Observable<boolean>) => (input: UntypedFormControl) =>
		input.value ? validationFn(input.value).pipe(map((exists) => (exists ? { exists } : null))) : of(null),

	matchesRegex:
		(regex: string, errorKey: string, match = true) =>
		(control: AbstractControl) =>
			control.value && new RegExp(regex).test(control.value) === match ? null : { [errorKey]: false },

	email: (control: AbstractControl): EmailValidityType => {
		if (!control.value || isEmailValid(control.value)) {
			return null;
		}

		return {
			emailInvalid: false,
		};
	},

	currencyAmount:
		(key = "format") =>
		(control: AbstractControl): CurrencyAmountValidityType =>
			control.value && control.value.toString().match(validationRegex.currencyAmount) ? null : { [key]: false },

	numericNotZero: (control: AbstractControl): NumericNotZeroValidityType =>
		control.value && control.value.toString().match(validationRegex.numberNotZero)
			? null
			: { numericNotZero: false },

	numberSmallerOrEqual: numberSatisfies<NumberSmallerOrEqualValidityType>((x, y) => x <= y, "numberSmallerOrEqual"),

	numberBiggerOrEqual: numberSatisfies<NumberBiggerOrEqualValidityType>((x, y) => x >= y, "numberBiggerOrEqual"),

	maxValue: numberSatisfies<MaxValueValidityType>((x, y) => x <= y, "maxValueExceeded"),

	dateEarlierOrEqual: dateSatisfies<dateEarlierOrEqualValidityType>((x, y) => x <= y, "dateEarlierOrEqual"),

	dateLaterOrEqual: dateSatisfies<dateLaterOrEqualValidityType>((x, y) => x >= y, "dateLaterOrEqual"),
};

function numberSatisfies<T extends { [key: string]: boolean } | null>(
	compareFn: (x: number | string, y: number | string) => boolean,
	error: keyof Exclude<T, null>
) {
	return (value: (() => number | string | undefined) | number | string | undefined) =>
		(control: AbstractControl): T | null => {
			const controlValue = +control.value;
			const compareValue = isFunction(value) ? value() : value;
			if (compareValue === null || compareValue === undefined || compareValue === "") {
				return null;
			}

			if (!Number.isNaN(controlValue) && compareFn(controlValue, +compareValue)) {
				return null;
			}

			return {
				[error]: false,
			} as T;
		};
}

function dateSatisfies<T extends { [key: string]: boolean } | null>(
	compareFn: (x: Date, y: Date) => boolean,
	error: keyof Exclude<T, null>
) {
	return (value: (() => string | Date | undefined) | string | Date | undefined) =>
		(control: AbstractControl): T | null => {
			const controlValue = new Date(control.value);
			const compareValue = isFunction(value) ? value() : value;
			if (
				compareValue === null ||
				compareValue === undefined ||
				compareValue === "" ||
				isNaN(new Date(compareValue!).getTime())
			) {
				return null;
			}

			if (!isNaN(controlValue.getTime()) && compareFn(controlValue, new Date(compareValue))) {
				return null;
			}

			return {
				[error]: false,
			} as T;
		};
}

function getPasswordValidity(jurisdiction: Jurisdiction | undefined): PasswordValidity {
	return {
		weak:
			jurisdiction === Jurisdiction.Aams
				? validationRegex.passwordStrength.medium
				: validationRegex.passwordStrength.weak,
		minimumLength: PASSWORD_MIN_LENGTH,
		maximumLength: undefined,
	};
}

export function checkboxRequired(minRequired = 1): ValidatorFn {
	return function validate(formGroup: UntypedFormGroup) {
		let checked = 0;

		Object.keys(formGroup.controls).forEach((key) => {
			const control = formGroup.controls[key];

			if (control.value) {
				checked++;
			}
		});

		return checked < minRequired
			? {
					required: true,
				}
			: null;
	};
}

export function getPasswordErrors(
	value: string,
	onlyFirstError: boolean,
	jurisdiction: Jurisdiction | undefined
): PasswordStrengthValidityType {
	const passwordValidity = getPasswordValidity(jurisdiction);

	if (isPasswordValid(value, passwordValidity)) {
		return null;
	}

	const result: PasswordStrengthValidityType = {};

	if (value.match(validationRegex.noNumber)) {
		result.passwordNumber = false;
		if (onlyFirstError) {
			return result;
		}
	}
	if (value.match(validationRegex.noUppercase)) {
		result.passwordUppercase = false;
		if (onlyFirstError) {
			return result;
		}
	}
	if (value.match(validationRegex.noLowercase)) {
		result.passwordLowercase = false;
		if (onlyFirstError) {
			return result;
		}
	}
	if (jurisdiction === Jurisdiction.Aams && !value.match(validationRegex.passwordSpecialChars)) {
		result.passwordSpecialChar = false;
		if (onlyFirstError) {
			return result;
		}
	}
	if (!value.match(validationRegex.passwordAllowedChars)) {
		result.passwordAllowedChars = false;
		if (onlyFirstError) {
			return result;
		}
	}

	if (value.length < passwordValidity.minimumLength && !onlyFirstError) {
		result.passwordMinimumLength = false;
	}

	if (passwordValidity.maximumLength && value.length > passwordValidity.maximumLength && !onlyFirstError) {
		result.passwordMaximumLength = false;
	}

	if (onlyFirstError) {
		return { passwordValid: false };
	}
	return result;
}

export const optionalFieldRegexValidator =
	(regex: string, errorCrsKey: string) =>
	(control: AbstractControl): { [errorCrsKey: string]: boolean } | null => {
		if (!control.value) {
			return null;
		}

		const regexTest = new RegExp(regex).test(control.value);

		if (!regexTest) {
			return { [errorCrsKey]: false };
		}

		return null;
	};

/**
 * Custom validator function that check the length of the control's value
 * with the given length and returns an error if it is not matches,
 * otherwise returns null.
 */
export function exactLengthValidator(length: number): ValidatorFn {
	return (control: AbstractControl): ExactLengthValidityType => {
		const value = control.value.toString();

		if (value && value.length !== length) {
			return { invalidLength: true };
		}

		return null;
	};
}

/**
 *
 * @param cities
 * @returns Validator Function
 * Custom validator function that checks if the control input for the city provided
 * belongs to the cities array provided as parameter.
 */
export function cityValidator(cities: string[]): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		const city = control.value;
		if (cities.includes(city)) {
			return null;
		}
		return { invalid: true };
	};
}
